import { atom, useAtom, useAtomValue } from 'jotai';
import { useRequest, useInterval } from 'ahooks';
import { getBotUsageSummary } from 'services/api/admin';
import { useEffect } from 'react';
import nimInst from 'services/nim';

type BotUsageSummary = typeof getBotUsageSummary extends () => Promise<infer R> ? R : never;

interface BotUsageSummarySelector extends BotUsageSummary {
  botPercentUsed: number;
  botUsageUrgent80: boolean;
  botUsageUrgent90: boolean;
}

const BotUsageSummaryState = atom<BotUsageSummarySelector>({} as BotUsageSummarySelector);

export const useBotUsageSummaryValue = () => {
  return useAtomValue(BotUsageSummaryState);
};

let refreshBotUsageSummaryFn = () => {};

export default function useBotUsageSummary() {
  const [data, setData] = useAtom(BotUsageSummaryState);
  const { loading, refresh } = useRequest(getBotUsageSummary, {
    onSuccess: (data) => {
      const { package: pack, sentNum, sendLimit } = data;
      const botPercentUsed = (() => {
        if (pack === 5) return 0;
        return sentNum ? Number(((sentNum / sendLimit) * 100).toFixed(1)) : 0;
      })();

      setData({
        ...data,
        botPercentUsed,
        botUsageUrgent80: botPercentUsed > 80,
        botUsageUrgent90: botPercentUsed > 90,
      });
    },
  });

  useEffect(() => {
    return nimInst.on('sys:message:bot_sent_limit_reach', () => {
      refresh();
    });
  }, [refresh]);

  useEffect(() => {
    refreshBotUsageSummaryFn = refresh;
  });

  useInterval(refresh, 1000 * 60 * 60);

  return [data, loading] as const;
}

export const refreshBotUsageSummary = () => {
  refreshBotUsageSummaryFn();
};
